import { useQuery } from '@apollo/client'
import {
  GetDisplayCoolerProductStatsQuery,
  GetDisplayCoolerProductStatsQueryVariables,
} from 'graphql/generated-types'

import { GET_DISPLAY_COOLER_PRODUCT_STATS } from '@/graphql'

type CoolerProductStat =
  GetDisplayCoolerProductStatsQuery['displayCoolerProductStats']['results'][number]
type DisplayCoolerProductStatsOrdering = NonNullable<
  GetDisplayCoolerProductStatsQueryVariables['ordering']
>

type Props = {
  displayId: string
  startDate: string
  endDate: string
  page: number
  rowsPerPage: number
  orderBy: keyof DisplayCoolerProductStatsOrdering
  order: 'ASC' | 'DESC'
}

const useCoolerProductStats = ({
  displayId,
  startDate,
  endDate,
  page,
  rowsPerPage,
  orderBy,
  order,
}: Props) => {
  const { data, loading, error } = useQuery<
    GetDisplayCoolerProductStatsQuery,
    GetDisplayCoolerProductStatsQueryVariables
  >(GET_DISPLAY_COOLER_PRODUCT_STATS, {
    variables: {
      displayId,
      startDate,
      endDate,
      pagination: {
        limit: rowsPerPage,
        offset: rowsPerPage * page,
      },
      ordering: {
        [orderBy]: order,
      },
    },
    skip: !displayId || !startDate || !endDate,
    fetchPolicy: 'cache-and-network',
  })

  const totalCount = data?.displayCoolerProductStats.totalCount ?? 0
  const allRowsLimit = totalCount > 0 ? totalCount : rowsPerPage

  const { data: allStatsData } = useQuery<
    GetDisplayCoolerProductStatsQuery,
    GetDisplayCoolerProductStatsQueryVariables
  >(GET_DISPLAY_COOLER_PRODUCT_STATS, {
    variables: {
      displayId,
      startDate,
      endDate,
      pagination: {
        limit: allRowsLimit,
        offset: 0,
      },
      ordering: {
        [orderBy]: order,
      },
    },
    skip: !displayId || !startDate || !endDate || totalCount === 0,
    fetchPolicy: 'cache-and-network',
  })

  const rows: CoolerProductStat[] =
    data?.displayCoolerProductStats.results || []
  const allRows: CoolerProductStat[] = allStatsData?.displayCoolerProductStats
    .results?.length
    ? allStatsData.displayCoolerProductStats.results
    : rows

  const trendByUpc = Object.fromEntries(
    allRows.map(row => [row.upc, row.revenueTrend]),
  ) as Record<string, number | null>

  const statsByUpc = Object.fromEntries(
    allRows.map(row => [
      row.upc,
      {
        name: row.name,
        revenue: row.revenue,
        revenueTrend: row.revenueTrend,
      },
    ]),
  ) as Record<
    string,
    {
      name: string | null
      revenue: number | null
      revenueTrend: number | null
    }
  >

  return {
    rows,
    trendByUpc,
    statsByUpc,
    loading,
    error: error?.message,
    totalCount: data?.displayCoolerProductStats.totalCount || 0,
  }
}

export default useCoolerProductStats
